import React, { HTMLProps, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updatePage } from '@store/page';
import { closeMobileMenu, mobileMenuSelector } from '@store/mobileMenu/index';

import Header from '../Header';
import SideMenuBase from '../SideNav/SideMenuBase';
import { ROUTES } from '../constants';

type PageLayoutProps = HTMLProps<HTMLDivElement> & { user?: any };

const FULL_WIDTH = ['/login', '/register', '/forgot-password', '/reset-password'];

const PageLayout: React.FunctionComponent<PageLayoutProps> = React.memo(({ children, user, ...restProps }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const mobileMenu = useSelector(mobileMenuSelector);

    const fullWidth = useMemo(() => FULL_WIDTH.some((path) => location.pathname.includes(path)), [location]);

    useEffect(() => {
        const route = ROUTES.find((r) => r.to === location.pathname);

        if (route) {
            // For my-account page, always show "Account"
            if (location.pathname === '/my-account') {
                dispatch(updatePage({ ...route, title: 'Account' }));
            } else {
                dispatch(updatePage(route));
            }
        }
    }, [location, dispatch, user]);

    // Close mobile menu when route changes
    useEffect(() => {
        if (mobileMenu.isOpen) {
            dispatch(closeMobileMenu());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, dispatch]);

    const handleBackdropClick = () => {
        if (mobileMenu.isOpen) {
            dispatch(closeMobileMenu());
        }
    };

    return fullWidth ? (
        <>{children}</>
    ) : (
        <div className="page d-flex flex-row flex-column-fluid" {...restProps}>
            {/* Mobile menu backdrop */}
            <div 
                className={`mobile-menu-backdrop d-lg-none ${mobileMenu.isOpen ? 'show' : ''}`}
                onClick={handleBackdropClick}
                style={{ display: mobileMenu.isOpen ? 'block' : 'none' }}
            />
            
            {/* Side menu with mobile class */}
            <div className={`${mobileMenu.isOpen ? 'mobile-menu-open' : ''}`}>
                <SideMenuBase user={user} />
            </div>
            
            <div className="wrapper d-flex flex-column flex-row-fluid">
                <Header headerText="" />
                {children}
            </div>
        </div>
    );
});

PageLayout.displayName = 'PageLayout';

export default PageLayout;
